import React, { useCallback, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import styled, { css, keyframes } from "styled-components";
import CustomTooltip from "../../common-components/CustomTooltip";
import { font } from "../../constants/font";
import { STATIC_PATH } from "../../constants/config";

const LIKE_TOOLTIP = "Like this clip";
const DISLIKE_TOOLTIP = "Dislike this clip";
const LIKE_DEFAULT_BORDER = "#BDBFC9";
const LIKE_DEFAULT_BG = "#F5F5F7";
const LIKED_BG = "#F81F43";
const LIKED_BORDER = "#AE4153";
const CARD_DISLIKE_ACTIVE_BORDER = "#9E9E9E";
const CARD_DISLIKE_ACTIVE_BG = "#EFEFEF";
const MODAL_DISLIKE_BORDER = "#44464F";
const MODAL_DISLIKE_BG = "#555761";

const getDislikeIconSrc = (variant, isDisliked) => {
  if (!isDisliked) return "dislike.svg";
  return variant === "onLight" ? "dislike-active-modal.svg" : "dislike-active.svg";
};

const floatUp = keyframes`
  0% {
    transform: translate(-50%, -50%) scale(0.3);
    opacity: 1;
  }
  30% {
    transform: translate(-50%, calc(-50% - 30px)) scale(0.6);
    opacity: 0.9;
  }
  60% {
    transform: translate(-50%, calc(-50% - 60px)) scale(1.2);
    opacity: 0.8;
  }
  100% {
    transform: translate(-50%, calc(-50% - 100px)) scale(0.8);
    opacity: 0;
  }
`;

const likePop = keyframes`
  0% {
    transform: scale(1);
  }
  35% {
    transform: scale(1.35);
  }
  65% {
    transform: scale(0.9);
  }
  100% {
    transform: scale(1);
  }
`;

const FloatingFavorite = styled.div`
  position: fixed;
  z-index: 10050;
  pointer-events: none;
  animation: ${floatUp} 1.5s ease-out forwards;

  img {
    width: 28px;
    height: 28px;
    filter: brightness(0) saturate(100%) invert(36%) sepia(89%) saturate(2582%)
      hue-rotate(331deg) brightness(99%) contrast(96%);
  }
`;

const FeedbackGroup = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const CircleButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;

  img {
    width: 20px;
    height: 20px;
    pointer-events: none;
    transition: filter 0.2s ease, transform 0.2s ease;
  }

  &:hover {
    transform: scale(1.05);
  }

  ${(props) => props.$variant === "onLight" && css`
    border: 1px solid #D7DDE0;
    background: ${props.theme.polarColor};
  `}

  ${(props) => props.$variant === "onDark" && css`
    border: 1px solid #5C5D62;
    background: rgba(22, 23, 32, 0.80);

    img {
      filter: brightness(0) invert(1);
    }
  `}
`;

const LikeCircleButton = styled(CircleButton)`
  ${(props) => props.$variant === "onLight" && !props.$isLiked && css`
    border: 1px solid ${LIKE_DEFAULT_BORDER};
    background: ${LIKE_DEFAULT_BG};
  `}

  ${(props) => props.$isLiked && css`
    background: ${LIKED_BG};
    border: 1px solid ${LIKED_BORDER};

    img {
      filter: none;
    }
  `}

  ${(props) => props.$animate && css`
    animation: ${likePop} 0.45s ease;

    img {
      animation: ${likePop} 0.45s ease;
    }
  `}
`;

const DislikeCircleButton = styled(CircleButton)`
  ${(props) => props.$variant === "onDark" && !props.$isDisliked && css`
    opacity: 0.8;
  `}

  ${(props) => props.$variant === "onDark" && props.$isDisliked && css`
    border: 1px solid ${CARD_DISLIKE_ACTIVE_BORDER};
    background: ${CARD_DISLIKE_ACTIVE_BG};
    opacity: 1;

    img {
      filter: none;
    }
  `}

  ${(props) => props.$variant === "onLight" && !props.$isDisliked && css`
    border: 1px solid ${LIKE_DEFAULT_BORDER};
    background: ${LIKE_DEFAULT_BG};

    img {
      filter: none;
    }
  `}

  ${(props) => props.$variant === "onLight" && props.$isDisliked && css`
    border: 1px solid ${MODAL_DISLIKE_BORDER};
    background: ${MODAL_DISLIKE_BG};

    img {
      filter: none;
    }
  `}
`;

const ShortsFeedbackButtons = ({
  feedbackType,
  onLike,
  onDislike,
  variant = "onDark",
  tooltipIdSuffix = "default",
  tooltipContext = "card",
  className,
}) => {
  const isLiked = feedbackType === "like";
  const isDisliked = feedbackType === "dislike";
  const [likeAnimating, setLikeAnimating] = useState(false);
  const [floatingFavorites, setFloatingFavorites] = useState([]);

  const likeTooltipId = useMemo(
    () => `shorts-like-tt-${tooltipContext}-${tooltipIdSuffix}`,
    [tooltipContext, tooltipIdSuffix],
  );
  const dislikeTooltipId = useMemo(
    () => `shorts-dislike-tt-${tooltipContext}-${tooltipIdSuffix}`,
    [tooltipContext, tooltipIdSuffix],
  );
  const dislikeIconSrc = useMemo(
    () => getDislikeIconSrc(variant, isDisliked),
    [variant, isDisliked],
  );

  const createFloatingFavorite = useCallback((x, y) => {
    const id = Date.now() + Math.random();
    setFloatingFavorites((prev) => [...prev, { id, x, y }]);

    setTimeout(() => {
      setFloatingFavorites((prev) => prev.filter((item) => item.id !== id));
    }, 1500);
  }, []);

  const handleLike = useCallback(
    (e) => {
      e.stopPropagation();
      e.preventDefault();

      if (!isLiked) {
        const rect = e.currentTarget.getBoundingClientRect();
        createFloatingFavorite(rect.left + rect.width / 2, rect.top + rect.height / 2);
        setLikeAnimating(true);
        setTimeout(() => setLikeAnimating(false), 450);
      }

      onLike?.();
    },
    [createFloatingFavorite, isLiked, onLike],
  );

  const handleDislike = useCallback(
    (e) => {
      e.stopPropagation();
      e.preventDefault();
      onDislike?.();
    },
    [onDislike],
  );

  const floatingFavoriteLayer = floatingFavorites.length > 0
    ? createPortal(
      <>
        {floatingFavorites.map((item) => (
          <FloatingFavorite
            key={item.id}
            style={{
              left: item.x,
              top: item.y,
            }}
          >
            <img src={`${STATIC_PATH}favorite.svg`} alt="" />
          </FloatingFavorite>
        ))}
      </>,
      document.body,
    )
    : null;

  return (
    <>
      {floatingFavoriteLayer}
      <FeedbackGroup className={className}>
        <CustomTooltip
          tooltipId={likeTooltipId}
          dataTooltip={LIKE_TOOLTIP}
          font={font.normal_medium_12}
          tooltipPosition="top"
          delayVisibilityBy={0}
          nonFormattedMessage
        >
          <LikeCircleButton
            type="button"
            className="feedback-btn like-btn"
            aria-label="Like"
            aria-pressed={isLiked}
            data-tooltip-id={likeTooltipId}
            $variant={variant}
            $isLiked={isLiked}
            $animate={likeAnimating}
            onClick={handleLike}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <img
              src={`${STATIC_PATH}${isLiked ? "favorite-active.svg" : "favorite.svg"}`}
              alt=""
            />
          </LikeCircleButton>
        </CustomTooltip>
        <CustomTooltip
          tooltipId={dislikeTooltipId}
          dataTooltip={DISLIKE_TOOLTIP}
          font={font.normal_medium_12}
          tooltipPosition="top"
          delayVisibilityBy={0}
          nonFormattedMessage
        >
          <DislikeCircleButton
            type="button"
            className="feedback-btn dislike-btn"
            aria-label="Dislike"
            aria-pressed={isDisliked}
            data-tooltip-id={dislikeTooltipId}
            $variant={variant}
            $isDisliked={isDisliked}
            onClick={handleDislike}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <img src={`${STATIC_PATH}${dislikeIconSrc}`} alt="" />
          </DislikeCircleButton>
        </CustomTooltip>
      </FeedbackGroup>
    </>
  );
};

ShortsFeedbackButtons.propTypes = {
  feedbackType: PropTypes.oneOf(["like", "dislike", null]),
  onLike: PropTypes.func,
  onDislike: PropTypes.func,
  variant: PropTypes.oneOf(["onDark", "onLight"]),
  tooltipIdSuffix: PropTypes.string,
  tooltipContext: PropTypes.oneOf(["card", "modal"]),
  className: PropTypes.string,
};

export default React.memo(ShortsFeedbackButtons);
